import React, { useState, useEffect, useMemo } from 'react';
import { Alert, Badge, Breadcrumb, Card, Col, Form, Row } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'helpers/api';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
// import AdvanceTableViewPatient from 'components/common/advance-table/AdvanceTable';
import AdvanceTableViewPatient from 'components/common/advance-table/AdvanceTableViewPatient.js';
import paths from 'routes/paths';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import moment from 'moment';
import IconItem from 'components/common/icon/IconItem';
import AddOrderModal from './AddOrderModal';
import Select from "react-select";
import ShimmerUI from 'components/ShimmerUI/PatientShimmer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import RefillDiscontinuedModal from 'components/prescription/RefillDiscontinuedModal';
import CommonBreadCrumb from 'components/commonbreadcrumb/CommonBreadCrumb';
import SetMastersDetails from 'components/common/SetMastersDetails';
import isHiddenFacility from 'helpers/isHiddenFacility';
import { isSharedLivingPatient } from 'helpers/isSharedLivingFacility';
import RemoveMasters from 'components/common/RemoveMasters';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';

const ViewPatient = () => {
    //const [facility] = useState(facilityStaffData);
    //const [facilityResults, setFacilityResults] = useState([]);
    const [patient, setPatient] = useState([]);
    const [masterPatient, setMasterPatient] = useState([]);
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showRefillModal, setShowRefillModal] = useState(false);
    const initialFacility = JSON.parse(localStorage.getItem("SelectedFacility")) || ({ key: "All Facilities", value: '' });
    const groupList = JSON.parse(localStorage.getItem("groupsData")) || [];
    let facilityResults = JSON.parse(localStorage.getItem("facilitiesData"));
    const defaultGroup = groupList.length > 0 ? groupList[0] : null;
    const facilitiesForGroup = defaultGroup
        ? facilityResults.filter(facility => facility.group_id === defaultGroup.value)
        : [];
    const defaultFacility = facilitiesForGroup.length > 0 ? facilitiesForGroup[0] : initialFacility;
    const [groupOptions, setGroupOptions] = useState([]);
    const [facilityobj, setFacilityObj] = useState({});
    const [getFacilitiesObj, setFacilitiesObj] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(defaultGroup);
    const [selectedFacility, setSelectedFacility] = useState(defaultFacility);
    const [isTable, setIsTable] = useState(true);
    const navigate = useNavigate();
    // const [propsData, setPropsData] = useState(null);
    // Show Nurse + Case Manager columns when at least one currently-visible row
    // sits at an SL-flagged facility (or has caregivers). DOB + Gender dropped
    // per request — those still live on the patient detail page.
    const showStaffingCols = useMemo(
        () => patient.some(p => isSharedLivingPatient(p)),
        [patient]
    );

    const columns = useMemo(() => {
        const base = [
            {
                accessorKey: 'individual',
                header: 'Individual',
                meta: {
                    headerProps: { className: 'text-900 text-start' },
                    cellProps: { className: 'text-start' }
                },
                cell: ({ row: { original } }) => {
                    const patientDetails = paths.patientDetails;
                    const sl = isSharedLivingPatient(original);
                    return (
                        <span className="d-inline-flex align-items-center gap-2">
                            <Link to={{ pathname: `${patientDetails}/${original.patientid}` }}>
                                {original.individual}
                                {original.dob && (
                                    <span className="text-500 ms-1">({original.dob})</span>
                                )}
                            </Link>
                            {/* UI-072: SL badge — backend exposes is_sharedliving_facility on each
                                patient row (Patient.getByFacilityId/getByGroupId LEFT JOIN) and
                                athome_caregivers count from the per-patient assignment list. */}
                            {/* {sl && (
                                <Badge bg="info" pill title="SharedLiving patient">SL</Badge>
                            )} */}
                        </span>
                    );
                }
            },
            {
                accessorKey: 'facilityname',
                header: 'Facility Name',
                meta: {
                    headerProps: { className: 'text-900 text-start w-10' },
                    cellProps: { className: 'text-start' }
                }
            }
        ];

        if (showStaffingCols) {
            base.push(
                {
                    accessorKey: 'nurse_name',
                    header: 'Nurse',
                    meta: {
                        headerProps: { className: 'text-900 text-start' },
                        cellProps: { className: 'text-start' }
                    },
                    cell: ({ row: { original } }) => {
                        if (!original.nurse_name) return <span className="text-400">—</span>;
                        return (
                            <div className="text-start">
                                <div>{original.nurse_name}</div>
                                {original.nurse_phone && (
                                    <a href={`tel:${original.nurse_phone}`} className="fs-11 text-decoration-none">
                                        {original.nurse_phone}
                                    </a>
                                )}
                            </div>
                        );
                    }
                },
                {
                    accessorKey: 'case_manager_name',
                    header: 'Case Manager',
                    meta: {
                        headerProps: { className: 'text-900 text-start' },
                        cellProps: { className: 'text-start' }
                    },
                    cell: ({ row: { original } }) => {
                        if (!original.case_manager_name) return <span className="text-400">—</span>;
                        return (
                            <div className="text-start">
                                <div>{original.case_manager_name}</div>
                                {original.case_manager_phone && (
                                    <a href={`tel:${original.case_manager_phone}`} className="fs-11 text-decoration-none">
                                        {original.case_manager_phone}
                                    </a>
                                )}
                            </div>
                        );
                    }
                },
                {
                    accessorKey: 'care_givers',
                    header: 'Care Givers',
                    meta: {
                        headerProps: { className: 'text-900 text-start' },
                        cellProps: { className: 'text-start' }
                    },
                }
            );
        }

        base.push(
            {
                accessorKey: 'addorder',
                header: 'Add Order',
                meta: {
                    headerProps: { className: 'text-900 text-start w-10' }
                },
                cell: ({ row: { original } }) => {
                    return <Link state={{
                        triggeredBy: "someAction",
                        data: original
                    }}>
                        <IconItem
                            tag="button"
                            icon={['far', 'edit']}
                            size="sm"
                            onClick={addOrderModal}
                            className="btn rounded-3 me-2 fs-11 align-items-center"
                        />
                    </Link>
                }
            },
            {
                accessorKey: '',
                header: 'Refill',
                meta: {
                    headerProps: { className: 'text-900 text-start' },
                    cellProps: { className: 'text-start' }
                },
                cell: ({ row: { original } }) => {
                    return <Link className="p-2" onClick={() => { setShowRefillModal(true) }}
                        state={{
                            triggeredBy: "someAction",
                            id: original.patientid,
                            dataObj: original
                        }}>
                        <FontAwesomeIcon icon="plus" className="me-1 fs-11" /></Link>
                }
            }
        );

        return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showStaffingCols]);

    const addOrderModal = () => {
        setShowModal(true);
    };

    useEffect(() => {

        /*let tempFacilityResults = JSON.parse(localStorage.getItem("facilitiesData"));

        setFacilityResults([
            { key: 'Select a facility', value: '' },
            ...tempFacilityResults
        ]);*/

        //fetchPatientData(tempFacilityResults[0].value);
        if (defaultFacility) {
            fetchPatientData(defaultFacility.value);
        } else {
            fetchPatientData('');
        }
        SetMastersDetails();
        return () => {
            RemoveMasters();
        }
    }, []);

    async function fetchPatientData(facilityid) {
        setLoading(true);
        try {
            let tokenStr = localStorage.getItem("accessToken");
            const { data } = await axios.get(`${process.env.REACT_APP_SERVER_URL}` + "/patients/list-all-patients/" + facilityid, { headers: { 'Authorization': `Bearer ${tokenStr}` } });
            let results = [];

            //console.log("Patient Data Status " + JSON.stringify(data));
            let objData = data.patients.data;
            let groupId = "";
            const groupList = JSON.parse(localStorage.getItem("groupsData"));
            const facilityResultsNew = (JSON.parse(localStorage.getItem("facilitiesData")) || []).filter(f => !isHiddenFacility(f));
            setOptions([
                { key: 'All facility', value: '' },
                ...facilityResultsNew
            ])
            if (localStorage.getItem("roleId") < 4) {
                let temp = {};
                const groupResults = groupList.map((group) => {
                    const facilitiesForGroup = facilityResults.filter(
                        (facility) => facility.group_id === group.value
                    );

                    temp[group.value] = facilitiesForGroup.map((facility) => ({
                        name: facility.key,
                        id: facility.value,
                    }));

                    return { key: group.key, value: group.value };
                });

                setGroupOptions([{ key: "Select Group", value: "" }, ...groupResults]);
                setFacilityObj(temp);
            }
            //console.log("patientObjData" + JSON.stringify(objData));

            results = objData
                .map((value) => {
                    groupId = value.group_id;
                    let genderName = value.gender == 1 ? "Male" : value == 2 ? "Female" : "Other";
                    let filteredFacilityName = getFacilityName(value.facility_id, facilityResults);

                    return {
                        individual: value.last_name + ", " + value.first_name,
                        dob: value.dob != null ? moment(value.dob, 'YYYY-MM-DD').format('MM/DD/YY') : null,
                        gender: genderName,
                        neworder: 'New Order',
                        medsheet: '',
                        treatment: '',
                        view: '',
                        facilityid: value.facility_id,
                        facilityname: filteredFacilityName,
                        patientid: value.id,
                        first_name: value.first_name,
                        last_name: value.last_name,
                        gender: genderName,
                        admit_date: value.admit_date,
                        physical_exam_date: value.physical_exam_date,
                        ssno: value.social_security_number,
                        allergies: value.allergies,
                        medsheet: value.medsheet_note,
                        efsno: value.insurance_efs_information,
                        insgrpno: value.insurance_group_no,
                        insno: value.insurance_no,
                        inspriority: value.insurance_priority,
                        is_program: value.not_capable_selfmeditating,
                        is_medicate: value.medication_independent_status,
                        is_active: value.is_active,
                        // UI-072: SL badge inputs — backend Patient.getByGroupId /
                        // getByFacilityId LEFT JOIN expose is_sharedliving_facility
                        // on each row. athome_caregivers is a count proxy via the
                        // patient's athome assignments (when present in the row).
                        is_sharedliving_facility: value.is_sharedliving_facility,
                        athome_caregivers_count: Array.isArray(value.athome_caregivers)
                            ? value.athome_caregivers.length
                            : 0,
                        // Nurse + Case Manager — surfaced when the patient sits at an
                        // SL-flagged facility (backend joins users for both).
                        nurse_name: [value.nurse_first_name, value.nurse_last_name].filter(Boolean).join(' ').trim(),
                        nurse_phone: value.nurse_phone || '',
                        case_manager_name: [value.case_manager_first_name, value.case_manager_last_name].filter(Boolean).join(' ').trim(),
                        case_manager_phone: value.case_manager_phone || '',
                        care_givers : value.caregivers?.length > 0 ? value.caregivers?.map((c) => `${c.last_name || ''} ${c.first_name || ''}`.trim()).join(', ') : 'No caregivers assigned'
                    };
                }).sort((a, b) => a.last_name.localeCompare(b.last_name));


            //localStorage.setItem("groupId", groupId);

            setPatient(results.filter(p => p.is_active));
            setMasterPatient(results);
        } catch (error) {
            setLoading(false);
            navigate('/error')
        } finally {
            setLoading(false);
        }
    }


    const handleGroupChange = (selectedGroup) => {
        setSelectedGroup(selectedGroup);
        const groupFacilityResults = facilityobj[selectedGroup.value]?.map((facility) => ({
            key: facility.name,
            value: facility.id,
        })) || [];
        setOptions([{ key: "All Facilities", value: '' }, ...groupFacilityResults]);
        setSelectedFacility(null);
    };

    function getFacilityName(facilityId, facilityList) {
        let facilityName = "";
        //Todo a break is required
        facilityList.forEach(function (singleFacility) {
            if (singleFacility.value == facilityId)
                facilityName = singleFacility.key;
        });
        return facilityName;
    }

    async function handleOptionSelect(selectedOption) {
        setSelectedFacility(selectedOption);
        localStorage.setItem("SelectedFacility", JSON.stringify(selectedOption));
        await fetchFacilityAfterSelection(selectedOption.value);
        if (Object.keys(facilityobj).length > 0) {
            const groupFacilityResults = facilityobj[selectedGroup.value]?.map((facility) => ({
                key: facility.name,
                value: facility.id,
            })) || [];
            setOptions([{ key: "All Facilities", value: '' }, ...groupFacilityResults]);
        }
    };

    const fetchFacilityAfterSelection = async (facilityId) => {
        setLoading(true);
        try {
            let tokenStr = localStorage.getItem("accessToken");
            if (Number(localStorage.getItem("roleId")) < 4) {
                if (facilityId == "") {
                    setIsTable(false);
                    return
                }
            }
            const { data } = await axios.get(`${process.env.REACT_APP_SERVER_URL}` + "/patients/list-all-patients/" + facilityId, { headers: { 'Authorization': `Bearer ${tokenStr}` } });
            let results = [];
            let objData = data?.patients?.data;
            if (objData.length > 0) {
                setIsTable(true);
            } else {
                setIsTable(false);
            }
            results = objData
                .map((value) => {
                    let genderName = value.gender == 1 ? "Male" : value == 2 ? "Female" : "Other";
                    let filteredFacilityName = getFacilityName(value.facility_id, facilityResults);

                    return {
                        individual: value.last_name + ", " + value.first_name,
                        dob: value.dob != null ? moment(value.dob, 'YYYY-MM-DD').format('MM/DD/YY') : null,
                        gender: genderName,
                        neworder: 'New Order',
                        medsheet: '',
                        treatment: '',
                        view: '',
                        facilityid: value.facility_id,
                        facilityname: filteredFacilityName,
                        patientid: value.id,
                        first_name: value.first_name,
                        last_name: value.last_name,
                        gender: genderName,
                        admit_date: value.admit_date,
                        physical_exam_date: value.physical_exam_date,
                        ssno: value.social_security_number,
                        allergies: value.allergies,
                        medsheet: value.medsheet_note,
                        efsno: value.insurance_efs_information,
                        insgrpno: value.insurance_group_no,
                        insno: value.insurance_no,
                        inspriority: value.insurance_priority,
                        is_program: value.not_capable_selfmeditating,
                        is_medicate: value.medication_independent_status,
                        is_active: value.is_active,
                        // UI-072: SL badge inputs — backend Patient.getByGroupId /
                        // getByFacilityId LEFT JOIN expose is_sharedliving_facility
                        // on each row. athome_caregivers is a count proxy via the
                        // patient's athome assignments (when present in the row).
                        is_sharedliving_facility: value.is_sharedliving_facility,
                        athome_caregivers_count: Array.isArray(value.athome_caregivers)
                            ? value.athome_caregivers.length
                            : 0,
                        // Nurse + Case Manager — surfaced when the patient sits at an
                        // SL-flagged facility (backend joins users for both).
                        nurse_name: [value.nurse_first_name, value.nurse_last_name].filter(Boolean).join(' ').trim(),
                        nurse_phone: value.nurse_phone || '',
                        case_manager_name: [value.case_manager_first_name, value.case_manager_last_name].filter(Boolean).join(' ').trim(),
                        case_manager_phone: value.case_manager_phone || '',
                    };
                }).sort((a, b) => a.last_name.localeCompare(b.last_name));
            setPatient(results.filter(p => p.is_active));
            setMasterPatient(results);
        } catch (error) {
            setLoading(false);
            navigate('/error')
        } finally {
            setLoading(false);
        }
    };

    const handlefilterChange = (e) => {
        const value = e.target.value;
        if (value === "active") {
            const activePatients = masterPatient.filter(p => p.is_active);
            setPatient(activePatients);
        } else if (value === "inactive") {
            const inactivePatients = masterPatient.filter(p => !p.is_active);
            setPatient(inactivePatients);
        } else {
            setPatient(masterPatient);
        }
    }

    const table = useAdvanceTable({
        data: patient,
        columns,
        selection: false,
        sortable: true,
        pagination: true
    });

    const breadcrumbItems = [
        { name: 'Dashboard', href: '/main', isActive: false },
        { name: 'Patient', href: paths.viewPatient, isActive: true }
    ];

    return (
        <>
            <CommonBreadCrumb breadcrumbItems={breadcrumbItems} />
            {showRefillModal && <RefillDiscontinuedModal show={showRefillModal} setShow={setShowRefillModal} />}
            {showModal && <AddOrderModal show={showModal} setShow={setShowModal} />}
            <Card>
                <Card.Body className="p-3">
                    <Row className='align-items-end mb-3'>
                        {Number(localStorage.getItem("roleId") < 4) ?
                            <Col xs="3" className="mb-0">
                                <Form.Group>
                                    <Form.Label>Group</Form.Label>
                                    <ThemedSelect
                                        name="group"
                                        closeMenuOnSelect={true}
                                        placeholder='Select Group'
                                        classNamePrefix="react-select"
                                        options={groupOptions}
                                        value={selectedGroup}
                                        getOptionLabel={option => option.key}
                                        getOptionValue={option => option.value}
                                        onChange={(e) => handleGroupChange(e)}
                                        styles={{
                                            control: (provided) => ({
                                                ...provided,
                                                borderColor: "#ced4da",
                                                boxShadow: "none",
                                                "&:hover": { borderColor: "#80bdff" },
                                            }),
                                            option: (provided, { isFocused, isSelected }) => ({
                                                ...provided,
                                                backgroundColor: isSelected ? "#e9ecef !important" : isFocused ? "#f8f9fa !important" : "white !important",
                                                color: isSelected ? "#212529 !important" : "#212529 !important",
                                                cursor: "pointer",
                                            }),
                                            singleValue: (provided) => ({
                                                ...provided,
                                                color: "#212529",
                                            }),
                                        }}
                                    />
                                </Form.Group>
                            </Col>
                            : []}
                        <Col xs={Number(localStorage.getItem("roleId") < 4) ? "3" : "6"} className="mb-0">
                            <Form.Group>
                                <Form.Label>Facility</Form.Label>
                                <ThemedSelect
                                    name="facility"
                                    closeMenuOnSelect={true}
                                    placeholder='Select Facility'
                                    classNamePrefix="react-select"
                                    options={options}
                                    value={selectedFacility}
                                    getOptionLabel={option => option.key}
                                    getOptionValue={option => option.value}
                                    onChange={(e) => handleOptionSelect(e)}
                                    styles={{
                                        control: (provided) => ({
                                            ...provided,
                                            borderColor: "#ced4da",
                                            boxShadow: "none",
                                            "&:hover": { borderColor: "#80bdff" },
                                        }),
                                        option: (provided, { isFocused, isSelected }) => ({
                                            ...provided,
                                            backgroundColor: isSelected ? "#e9ecef !important" : isFocused ? "#f8f9fa !important" : "white !important",
                                            color: isSelected ? "#212529 !important" : "#212529 !important",
                                            cursor: "pointer",
                                        }),
                                        singleValue: (provided) => ({
                                            ...provided,
                                            color: "#212529",
                                        }),
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs="3" className="mb-0 view-patient-search-holder">
                            <AdvanceTableProvider {...table}>
                                <AdvanceTableSearchBox placeholder="Search..." />
                            </AdvanceTableProvider>
                        </Col>
                        <Col xs='3' className="mb-0">
                            <Form.Label>Filter</Form.Label>
                            <Form.Select
                                name="sortby"
                                onChange={handlefilterChange}
                                defaultValue='active'
                            >
                                <option value="">All</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </Form.Select>
                        </Col>
                    </Row>
                    <Row>
                        <Col></Col>
                        <Col></Col>
                    </Row>
                </Card.Body>
            </Card>
            {loading ? <ShimmerUI /> : patient.length > 0 ?
                <>
                    <AdvanceTableProvider {...table}>
                        <Card className="mb-3">
                            <Card.Body className="p-3">
                                {isTable ? <Row>
                                    <Col xs="12" className="mb-1 mt-1">
                                        <AdvanceTableViewPatient
                                            headerClassName="bg-200 text-nowrap align-middle text-center"
                                            rowClassName="text-nowrap align-middle text-center"
                                            tableProps={{
                                                size: 'sm',
                                                striped: true,
                                                className: 'fs-10 mb-0 overflow-hidden'
                                            }}
                                        />
                                    </Col>
                                </Row> :
                                    <div className="d-flex justify-content-center align-items-center text-center" style={{ height: '50px' }}>
                                        <p className="text-danger fw-bold mb-0">There is no data available for this option, Please select another one</p>
                                    </div>
                                }
                            </Card.Body>
                            <Card.Footer>
                                <div className="mt-3">
                                    <AdvanceTableFooter
                                        rowInfo
                                        navButtons
                                        rowsPerPageSelection
                                    />
                                </div>
                            </Card.Footer>
                        </Card>
                    </AdvanceTableProvider>
                </> : (<Alert variant="warning" className="text-center">
                    <h5 className="mb-0">No Patients Found</h5>
                </Alert>)
            }
        </>
    );
};

export default ViewPatient;
